
import { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { WebsiteSection } from './types/section-types';
import { SectionSelect } from './SectionSelect';
import { UploadButton } from './UploadButton';

interface StandardImageUploadProps {
  defaultSection?: WebsiteSection;
  defaultSubsection?: string;
}

export const StandardImageUpload = ({ defaultSection, defaultSubsection }: StandardImageUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [section, setSection] = useState<WebsiteSection>(defaultSection || 'about-me');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('Bitte wählen Sie eine Datei aus');
      }

      setUploading(true);
      const file = event.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Die Datei darf nicht größer als 5MB sein');
      }

      if (!file.type.startsWith('image/')) {
        throw new Error('Bitte wählen Sie nur Bilddateien aus');
      }
      
      // Für alle anderen Bilder verwenden wir den Standard-Supabase-Storage-Upload
      const fileExt = file.name.split('.').pop();
      const timestamp = new Date().getTime();
      const filePath = `${timestamp}-${Math.random()}.${fileExt}`;

      // Upload zu Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        throw new Error('Fehler beim Hochladen: ' + uploadError.message);
      }

      // Referenz in der Datenbank speichern
      const currentSection = defaultSection || section;
      const { error: dbError } = await supabase
        .from('images')
        .insert({
          storage_path: filePath,
          section: currentSection,
          subsection: defaultSubsection,
          title: file.name,
        })
        .select();

      if (dbError) {
        throw new Error('Fehler beim Speichern: ' + dbError.message);
      }

      toast.success('Bild erfolgreich hochgeladen');
      
      // Sonderfall Banner-Bild
      if (currentSection === 'new-banner') {
        console.log("Dispatching banner-image-uploaded event");
        window.dispatchEvent(new Event('banner-image-uploaded'));
        
        // Seite nach kurzer Verzögerung neu laden
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
      
      // Dateiauswahl zurücksetzen
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Fehler beim Hochladen des Bildes');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-4">
      {!defaultSection && (
        <SectionSelect value={section} onChange={setSection} />
      )}
      <UploadButton uploading={uploading} onUploadClick={handleUploadClick} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={uploadImage}
        disabled={uploading}
        className="hidden"
      />
    </div>
  );
};
